import React, { useState, useCallback, useMemo, useContext } from 'react';
import { createPortal } from 'react-dom';

// DEPENDENCIES
import clsx from 'clsx';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useFormikContext } from 'formik';
import { useDispatch } from 'react-redux';
import isNumber from 'lodash/isNumber';

// HELPERS
import { withRouter } from 'helpers/withRouter';
import useRecordView from 'helpers/getRecordView';

// GLOBAL HELPERS/CONTEXT
import { RecordContext } from 'helpers/getRecordData';

// GLOBAL FUNCTIONS
import { sidebarActions, lookupActions } from 'actions.js';

// GLOBAL VARIABLES
import { RETAIL_WHOLESALE } from 'codes';

// GLOBAL COMPONENTS
import Form from 'core/form/Form';
import Card from 'parts/card/Card';
import Sidebar from 'parts/sidebar/Sidebar';

// LOCAL COMPONENTS
import ToggledItems from '../tools/ToggledItems';
import TradeView from '../tools/TradeView';
import RetailTrade from './Trade/RetailTrade';
import WholeSaleTrade from './Trade/WholeSaleTrade';

// STYLES
import styles from './trade.module.scss';

// MAIN COMPONENT
const TradeForm = compose(
  withRouter,
  connect(({ lookups }) => ({ lookups }), { ...lookupActions })
)(
  ({
    // REDUX DISPATCH
    flush
  }) => {
    // CONTEXT
    const { record: { inventory = {}, ...record } = {}, updateContext } = useContext(RecordContext);
    const { values, setFieldValue } = useFormikContext() || {};
    const { isAdd } = useRecordView();
    const dispatch = useDispatch();

    // STATE
    const [isUpdate, setIsUpdate] = useState(false);
    const [index, setindex] = useState('');
    const [title, setTitle] = useState('');

    // MEMOS
    const { aryTrade = [], sale_type_category_id } = useMemo(() => values || {}, [values]);
    const { stock_num } = useMemo(
      () => (isAdd ? record : inventory),
      [record, inventory, isAdd]
    );

    const isWholesale = useMemo(
      () => parseInt(sale_type_category_id) === RETAIL_WHOLESALE.wholesale.numeric,
      [sale_type_category_id]
    );
    const isRetail = useMemo(
      () => parseInt(sale_type_category_id) === RETAIL_WHOLESALE.retail.numeric,
      [sale_type_category_id]
    );

    const handleClick = useCallback(
      (data = aryTrade, i) => {
        setindex(data.indexOf(i));
        setTitle('Edit Trade');
        flush('exteriorColors');
        flush('interiorColors');
        dispatch(sidebarActions.toggle('tradeSideBar', true));
      },
      [dispatch, setindex, aryTrade, flush]
    );

    const nextClick = useCallback(() => {
      setindex(aryTrade.length ? aryTrade.length : 0);
      setTitle(isWholesale ? 'New Trade / DX' : isRetail ? 'New Trade' : '');
      flush('exteriorColors');
      flush('interiorColors');
      setIsUpdate(!isUpdate);
      const { customer_first_name, customer_last_name } = values ?? {};
      updateContext({ type: 'updateRecord', customer_first_name, customer_last_name });
    }, [setIsUpdate, setindex, isWholesale, isRetail, isUpdate, aryTrade, flush, updateContext, values]);

    const handleCancel = useCallback(() => {
      const newitems = aryTrade?.filter(item => isNumber(item?.inventory?.daily_sales_inventory_id));
      setFieldValue('aryTrade', newitems);
      dispatch(sidebarActions.close());

      setindex('');
    }, [dispatch, aryTrade, setFieldValue, setindex]);

    const handleSubmit = useCallback(
      record => {
        const { aryTrade } = record || {};
        setFieldValue('aryTrade', aryTrade);
        aryTrade.forEach((item, i) => {
          const flag = item?.trade_id;
          if (!flag) {
            if (isWholesale) {
              setFieldValue(`aryTrade[${i}].acv`, 0);
              setFieldValue(`aryTrade[${i}].trade_in_allowance`, 0);
              setFieldValue(`aryTrade[${i}].inventory.new_used`, 1);
              setFieldValue(`aryTrade[${i}].inventory.inventory_source_id`, 8);
            }
            if (!isWholesale) setFieldValue(`aryTrade[${i}].inventory.new_used`, 0);
            setFieldValue(`aryTrade[${i}].inventory.daily_sales_inventory_id`, 0);
            setFieldValue(`aryTrade[${i}].inventory.aryInventoryDealerPack`, []);
          }
        });
        dispatch(sidebarActions.close());
        document.getElementById('trade-section').scrollIntoView({ behavior: 'smooth' });
      },
      [setFieldValue, isWholesale, dispatch]
    );
    // RENDER
    return (
      <Form.Body>
        <Form.Col>
          <ToggledItems name="aryTrade" flagName="has_trade" label="Trade to add?" itemLabel="Trade">
            <Sidebar.Button
              className="m-3"
              variant="secondary"
              name="tradeSideBar"
              icon="add-circle"
              label="Add Trade"
              onClick={nextClick}
              fullWidth={false}
              size="sm"
              outline
            />
            {createPortal(
              <Sidebar
                name="tradeSideBar"
                title={title}
                className={styles.sidebar}
                closeButton={{ onClick: handleCancel }}
              >
                {isWholesale ? (
                  <WholeSaleTrade
                    initialValues={values || {}}
                    handleCancel={handleCancel}
                    handleSubmit={handleSubmit}
                    index={index}
                  />
                ) : (
                  <RetailTrade
                    initialValues={values || {}}
                    stockNum={stock_num}
                    handleCancel={handleCancel}
                    handleSubmit={handleSubmit}
                    index={index}
                  />
                )}
              </Sidebar>,
              document.body
            )}

            <TradeView data={aryTrade} Open={handleClick} />
          </ToggledItems>
        </Form.Col>
      </Form.Body>
    );
  }
);

export default function Trade(props) {
  return (
    <div className="position-relative">
      <div id="trade-section" className={clsx('position-absolute', styles.anchor)} />
      <Card title="Trade Info" body={<TradeForm {...props} />} />
    </div>
  );
}
